import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Location {
  id: string;
  name: string;
  address: string;
  type: 'main' | 'branch' | 'satellite';
  isActive: boolean;
  createdAt: Date;
  providerNumbers: string[]; // Provider numbers associated with this location
}

interface LocationContextType {
  locations: Location[];
  activeLocation: Location | null;
  allLocationsMode: boolean;
  addLocation: (location: Omit<Location, 'id' | 'createdAt'>) => void;
  updateLocation: (id: string, updates: Partial<Location>) => void;
  deleteLocation: (id: string) => void;
  setActiveLocation: (id: string | null) => void;
  setAllLocationsMode: (enabled: boolean) => void;
  getLocationByProvider: (providerNumber: string) => Location | undefined;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const LOCATIONS_STORAGE_KEY = 'ltss_locations';
const ACTIVE_LOCATION_KEY = 'ltss_active_location';

export function LocationProvider({ children }: { children: ReactNode }) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [allLocationsMode, setAllLocationsMode] = useState(true);

  // Load locations from storage
  useEffect(() => {
    const savedLocations = localStorage.getItem(LOCATIONS_STORAGE_KEY);
    const savedActiveId = localStorage.getItem(ACTIVE_LOCATION_KEY);
    
    if (savedLocations) {
      try {
        const parsed = JSON.parse(savedLocations);
        setLocations(parsed.map((loc: any) => ({
          ...loc,
          createdAt: new Date(loc.createdAt),
        })));
      } catch (e) {
        console.error('Failed to load locations:', e);
      }
    }
    
    if (savedActiveId) {
      setActiveLocationId(savedActiveId);
      setAllLocationsMode(false);
    }
  }, []);

  // Save locations to storage
  const saveLocations = (locs: Location[]) => {
    localStorage.setItem(LOCATIONS_STORAGE_KEY, JSON.stringify(locs));
    setLocations(locs);
  };

  const addLocation = (locationData: Omit<Location, 'id' | 'createdAt'>) => {
    const newLocation: Location = {
      ...locationData,
      id: Date.now().toString(),
      createdAt: new Date(),
    };
    saveLocations([...locations, newLocation]);
  };

  const updateLocation = (id: string, updates: Partial<Location>) => {
    saveLocations(locations.map(loc => 
      loc.id === id ? { ...loc, ...updates } : loc
    ));
  };

  const deleteLocation = (id: string) => {
    saveLocations(locations.filter(loc => loc.id !== id));
    if (activeLocationId === id) {
      setActiveLocationId(null);
      setAllLocationsMode(true);
      localStorage.removeItem(ACTIVE_LOCATION_KEY);
    }
  };

  const setActiveLocation = (id: string | null) => {
    setActiveLocationId(id);
    if (id) {
      localStorage.setItem(ACTIVE_LOCATION_KEY, id);
      setAllLocationsMode(false);
    } else {
      localStorage.removeItem(ACTIVE_LOCATION_KEY);
      setAllLocationsMode(true);
    }
  };

  const getLocationByProvider = (providerNumber: string): Location | undefined => {
    return locations.find(loc => loc.providerNumbers.includes(providerNumber));
  };

  const activeLocation = activeLocationId 
    ? locations.find(loc => loc.id === activeLocationId) || null 
    : null;

  return (
    <LocationContext.Provider value={{
      locations,
      activeLocation,
      allLocationsMode,
      addLocation,
      updateLocation,
      deleteLocation,
      setActiveLocation,
      setAllLocationsMode,
      getLocationByProvider,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocations() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocations must be used within a LocationProvider');
  }
  return context;
}
